//
// # DrcPanel
//
// Panel showing DRC (Design Rule Check) results.
// Displays errors grouped by type with click-to-highlight functionality.
//
// Click an error row to highlight the relevant layout instances.
//

import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  EyeOff,
  Play,
} from "lucide-react";
import typia from "typia";
import { validate as v } from "CoreUtils";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import { useDrcResults } from "./useDrcResults";
import { DrcErrorRow } from "./DrcErrorRow";
import "./drcPanel.css";

// Interface for parsed identKey with path
interface IdentKeyWithPath {
  kind?: string;
  path: string[];
}

// Validator for IdentKeyWithPath
const IdentKeyWithPathValidator = {
  validator: new v.Validator(typia.createValidate<IdentKeyWithPath>()),
};

// Format identKey for display: {"kind":"hierpath","path":["lib","cell","view"]} -> "lib/cell/view"
function formatIdentKey(identKey: string): string {
  try {
    const parsed: unknown = JSON.parse(identKey);
    const result = IdentKeyWithPathValidator.validator.validate(parsed);
    if (result.ok) {
      return result.val.path.join("/");
    }
  } catch {
    // Fall through to return as-is
  }
  return identKey;
}

export function DrcPanel() {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const { result, loading } = useDrcResults();
  const [running, setRunning] = useState(false);

  // Clear highlights when panel unmounts
  useEffect(() => {
    return () => {
      workspace.highlightDrcError(null);
    };
  }, [workspace]);

  const handleClearHighlights = () => {
    workspace.highlightDrcError(null);
  };

  const handleRunDrc = async () => {
    setRunning(true);
    try {
      // Result is intentionally not checked here - workspace.runDrc displays
      // errors via the status bar, and DRC results arrive via WebSocket broadcast.
      await workspace.runDrc();
    } finally {
      setRunning(false);
    }
  };

  // Count errors by type
  const errorCounts = result?.errors?.reduce<Record<string, number>>(
    (acc, err) => {
      acc[err.kind] = (acc[err.kind] ?? 0) + 1;
      return acc;
    },
    {}
  );

  const totalErrors = result?.errors?.length ?? 0;
  const passed = result?.passed ?? false;

  // Check if active editor is a layout
  const isLayoutActive =
    snapshot.activeIdent &&
    workspace.getActiveEditor()?.content.contentKind === "layout";

  const isRunning = running || loading;

  const drcButtonClass =
    "flex-1 px-3 py-1.5 rounded flex items-center justify-center gap-1.5 text-[11px] " +
    "bg-[var(--color-ide-surface-hover)] hover:bg-[var(--color-ide-surface-selected)] " +
    "text-[var(--color-ide-text)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

  return (
    <div className="flex flex-col h-full bg-[var(--color-ide-surface)] text-[var(--color-ide-text)]">
      {/* Panel header */}
      <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-muted)] select-none border-b border-[var(--color-ide-border)]">
        <div className="flex items-center gap-1.5">
          <AlertTriangle size={11} />
          DRC Results
          {result?.identKey && (
            <span className="ml-1 font-normal normal-case tracking-normal text-[var(--color-ide-text-dim)]">
              — {formatIdentKey(result.identKey)}
            </span>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {/* Run DRC button */}
        {isLayoutActive && (
          <div className="p-2 flex gap-2 border-b border-[var(--color-ide-border)]">
            <button
              className={drcButtonClass}
              onClick={() => void handleRunDrc()}
              disabled={isRunning}
              title="Run design rule checks on the layout"
            >
              <Play size={12} />
              Run DRC
            </button>
          </div>
        )}

        {isRunning && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-muted)]">
            Running DRC...
          </div>
        )}

        {!isLayoutActive && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-dim)]">
            Open a layout to run DRC.
          </div>
        )}

        {isLayoutActive && !isRunning && !result && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-dim)]">
            No DRC results. Click the button above to check the layout for
            design rule violations.
          </div>
        )}

        {!isRunning && result && (
          <div className="p-2">
            {/* Status banner */}
            <div
              className={`px-3 py-2 mb-2 rounded flex items-center gap-2 text-[11px] ${
                passed
                  ? "bg-green-500/10 text-green-400"
                  : "bg-red-500/10 text-red-400"
              }`}
            >
              {passed ? <CheckCircle size={14} /> : <XCircle size={14} />}
              {passed
                ? "DRC Clean"
                : `${String(totalErrors)} Error${
                    totalErrors !== 1 ? "s" : ""
                  } Found`}
            </div>

            {/* Error summary */}
            {!passed && errorCounts && (
              <div className="px-2 pb-2 text-[10px] text-[var(--color-ide-text-muted)] flex flex-wrap gap-2">
                {errorCounts.instance_overlap && (
                  <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                    {errorCounts.instance_overlap} Overlap
                    {errorCounts.instance_overlap !== 1 ? "s" : ""}
                  </span>
                )}
                {errorCounts.min_spacing && (
                  <span className="px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
                    {errorCounts.min_spacing} Spacing
                  </span>
                )}
                {errorCounts.min_width && (
                  <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                    {errorCounts.min_width} Width
                  </span>
                )}
                {errorCounts.parameter_violation && (
                  <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                    {errorCounts.parameter_violation} Parameter
                    {errorCounts.parameter_violation !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            )}

            {/* Error list */}
            {result.errors && result.errors.length > 0 && (
              <div className="flex flex-col gap-1">
                {/* Clear highlights button */}
                <button
                  className="w-full px-2 py-1 rounded flex items-center gap-2 text-[10px] text-[var(--color-ide-text-muted)] hover:bg-[var(--color-ide-surface-hover)] transition-colors"
                  onClick={handleClearHighlights}
                >
                  <EyeOff size={10} />
                  Clear highlights
                </button>
                {result.errors.map((error, index) => (
                  <DrcErrorRow key={index} error={error} index={index} />
                ))}
              </div>
            )}

            {/* Stats */}
            {result.stats && (
              <div className="mt-3 px-2 text-[10px] text-[var(--color-ide-text-dim)]">
                <div>
                  Instances checked: {String(result.stats.instancesChecked)}
                </div>
                <div>Rules checked: {String(result.stats.rulesChecked)}</div>
                <div>
                  Overlapping pairs: {String(result.stats.overlappingPairs)}
                </div>
                {result.stats.skipped?.map((s, i) => (
                  <div key={i}>
                    Skipped {s.rule}: {s.reason}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
